import { useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { AppDispatch } from '../../store/store';

import { socketClient } from '../socketClient';

import { subscribeWhenConnected } from '../utils/subscribeWhenConnected';

import {
    clearRealtimeState,
    setRealtimeState,
} from '../../store/slices/sale-dte/saleDteRealtimeSlice';

import { SaleDteRealtimeMessage } from '../../types/websocket/SaleDteRealtimeMessage';
import { updateSaleDteStatus } from '../../store/slices/sale/saleSlice';
import { showModal } from '../../store/slices/modals/modalsSlice';

interface Props {
    enabled: boolean;
}

export const useSalesDteSubscription = ({
    enabled,
}: Props) => {

    const dispatch: AppDispatch = useDispatch();

    const subscribedRef = useRef(false);

    const subscriptionRef = useRef<any>(null);

    const timeoutRef = useRef<any>(null);

    useEffect(() => {

        // =====================================================
        // 🚫 CANAL DESHABILITADO
        // =====================================================

        if (!enabled) {

            subscriptionRef.current?.unsubscribe();

            subscribedRef.current = false;

            return;
        }

        // =====================================================
        // 🚫 EVITAR MULTI SUBSCRIBE
        // =====================================================

        if (subscribedRef.current) {
            return;
        }

        // =====================================================
        // 📡 SUBSCRIBE
        // =====================================================

        subscribeWhenConnected(() => {

            if (subscribedRef.current) {
                return;
            }

            subscribedRef.current = true;

            subscriptionRef.current = socketClient.subscribe(

                '/topic/sales/dte-status',

                (message) => {

                    if (!message.body) {
                        return;
                    }

                    try {

                        const payload: SaleDteRealtimeMessage =
                            JSON.parse(message.body);

                        console.log(
                            '📡 DTE realtime:',
                            payload
                        );

                        dispatch(
                            setRealtimeState(payload)
                        );

                        const processedStatuses = ['ACCEPTED', 'PROCESADO', 'DTE_PROCESSED'];
                        const rejectedStatuses = ['REJECTED', 'RECHAZADO', 'DTE_REJECTED'];
                        let dteStatus: string | null = null;
                        let hasDteError = false;

                        if (processedStatuses.includes(payload.status)) dteStatus = 'DTE_PROCESSED';
                        if (rejectedStatuses.includes(payload.status)) {
                            dteStatus = 'DTE_REJECTED';
                            hasDteError = true;
                        }
                        if (payload.status === 'DTE_PENDING' || payload.status === 'DTE_SENT') {
                            dteStatus = payload.status;
                        }

                        if (dteStatus) {
                            dispatch(updateSaleDteStatus({ id: payload.saleId, dteStatus, hasDteError }));
                        }

                        const observations = payload.observations?.length
                            ? `\n\nObservaciones:\n${payload.observations.join('\n')}`
                            : '';
                        const code = payload.code ? `[${payload.code}] ` : '';
                        const pendingHelp = payload.status === 'DTE_PENDING'
                            ? '\n\nLa venta fue emitida en contingencia. El sistema la enviará automáticamente cuando Hacienda esté disponible.'
                            : '';
                        const rejectedHelp = rejectedStatuses.includes(payload.status)
                            ? '\n\nPuede consultar y descargar la respuesta completa desde el historial de ventas.'
                            : '';

                        if (payload.message || payload.title) {
                            const statusType = payload.status === 'DTE_PENDING'
                                ? 'warning'
                                : rejectedStatuses.includes(payload.status)
                                    ? 'error'
                                    : processedStatuses.includes(payload.status)
                                        ? 'success'
                                        : payload.status === 'DTE_SENT'
                                            ? 'info'
                                        : 'info';
                            dispatch(showModal({
                                modalType: dteStatus
                                    ? statusType
                                    : payload.type === 'pending' ? 'info' : (payload.type || statusType),
                                modalProps: {
                                    title: payload.title || 'Actualización del estado DTE',
                                    message: `${code}${payload.message || 'Se actualizó el estado del documento.'}${observations}${pendingHelp}${rejectedHelp}`,
                                },
                            }));
                        }

                        if (timeoutRef.current) clearTimeout(timeoutRef.current);
                        timeoutRef.current = setTimeout(() => dispatch(clearRealtimeState()), 4000);

                    } catch (error) {

                        console.error(
                            '❌ Error realtime DTE:',
                            error
                        );
                    }
                }
            );

            console.log(
                '✅ Subscription DTE registrada'
            );
        });

        // =====================================================
        // 🧹 CLEANUP
        // =====================================================

        return () => {

            subscriptionRef.current?.unsubscribe();

            subscriptionRef.current = null;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            subscribedRef.current = false;

            console.log(
                '🛑 Subscription DTE removida'
            );
        };

    }, [enabled]);
};
